import { useEffect, useState } from "react";
import { Award, Lock, TrendingUp } from "lucide-react";
import { http } from "@/lib/api";
import { feelingColor, FEELINGS } from "@/lib/buddy";
import { FeelingIcon } from "@/components/buddy/FeelingIcon";

const CAT = { hygiene: "Self-care", routine: "Routines", feelings: "Feelings", safety: "Safety" };

const Skills = ({ title, week, testId }) => (
  <div className="rounded-2xl bg-[#F7F3EB] p-4" data-testid={testId}>
    <p className="text-xs uppercase tracking-widest text-stone-500">{title}</p>
    <p className="font-display text-2xl text-[#2C4A3E] mt-1">{week.skills.length} skills · {week.xp} XP</p>
    <ul className="mt-2 space-y-1 text-sm text-stone-700">
      {week.skills.map((s, i) => <li key={i}>• {s.title} <span className="text-xs text-stone-400">({CAT[s.category] || s.category})</span></li>)}
      {week.lessons_taught > 0 && <li>• Taught {week.lessons_taught} lesson{week.lessons_taught > 1 ? "s" : ""} to the buddy</li>}
      {!week.skills.length && !week.lessons_taught && <li className="text-stone-400">Nothing new yet</li>}
    </ul>
  </div>
);

const WeekChart = ({ weeks }) => {
  const max = Math.max(10, ...weeks.map((w) => w.xp));
  return (
    <div className="flex items-end gap-4 h-36 mt-4" data-testid="growth-week-chart">
      {weeks.map((w, i) => (
        <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1 h-full justify-end">
          <span className="text-xs text-stone-500">{w.xp}</span>
          <div className={`w-full rounded-t-xl ${i === weeks.length - 1 ? "bg-[#C87D53]" : "bg-[#6B9080]"}`} style={{ height: `${Math.max(4, (w.xp / max) * 100)}%` }} data-testid={`growth-week-${i}`} />
          <span className="text-[11px] text-stone-500">{i === weeks.length - 1 ? "This week" : w.week_start.slice(5)}</span>
        </div>
      ))}
    </div>
  );
};

const Moods = ({ checkins, low }) => (
  <div className="mt-6" data-testid="growth-moods">
    <p className="text-xs uppercase tracking-widest text-stone-500">Daily check-ins · last 14 days</p>
    <div className="flex flex-wrap gap-2 mt-3">
      {checkins.map((c) => (
        <span key={c.date} title={`${c.date}: ${c.feeling} · goal "${c.goal}"${c.goal_done ? " (done)" : ""}`} data-testid={`growth-mood-${c.date}`}
          className={`w-10 h-10 rounded-full grid place-items-center text-[#2C4A3E] ${c.goal_done ? "ring-2 ring-[#6B9080]" : ""}`} style={{ background: feelingColor(c.feeling) }}>
          <FeelingIcon feeling={c.feeling} className="w-4 h-4" />
        </span>
      ))}
      {!checkins.length && <span className="text-sm text-stone-400">No check-ins yet</span>}
    </div>
    <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-stone-600" data-testid="growth-mood-legend">
      {FEELINGS.map(([k, label, color]) => <span key={k} className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ background: color }} />{label}</span>)}
    </div>
    {low >= 3 && <p className="mt-3 text-sm text-red-700" data-testid="growth-low-mood-alert">{low} sad, worried or angry days this week. A gentle check-in could help.</p>}
    {checkins.length > 0 && <p className="text-xs text-stone-500 mt-2">Ringed = daily goal completed ({checkins.filter((c) => c.goal_done).length}/{checkins.length})</p>}
  </div>
);

export const BuddyGrowthReport = ({ classId, studentId }) => {
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/classes/${classId}/students/${studentId}/buddy/growth`).then((r) => setD(r.data)).catch(() => {}); }, [classId, studentId]);
  if (!d?.has_buddy) return null;
  const p = d.progress;
  return (
    <section className="mt-10 rounded-3xl bg-white/60 border border-[#E6DEC3] p-6" data-testid="buddy-growth-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-500 inline-flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Buddy growth</p>
          <h3 className="font-display text-2xl text-[#2C4A3E] mt-1" data-testid="growth-level">{d.buddy_name} · Level {p.level}</h3>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="growth-badges">
          {p.badges.map((b) => (
            <span key={b.id} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] ${b.earned ? "bg-[#F2D49B] text-[#5B3A1E]" : "bg-[#F1EADB] text-stone-600"}`}>
              {b.earned ? <Award className="w-3 h-3" /> : <Lock className="w-3 h-3" />}{b.name}
            </span>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <Skills title="This week" week={d.this_week} testId="growth-this-week" />
        <Skills title="Last week" week={d.last_week} testId="growth-last-week" />
      </div>
      <p className="text-xs uppercase tracking-widest text-stone-500 mt-6">XP earned teaching the buddy · last 4 weeks</p>
      <WeekChart weeks={d.weeks} />
      <Moods checkins={d.checkins} low={d.low_days_7} />
    </section>
  );
};
